import React, { useState, useEffect } from "react";
import JSONDisplay from "./JSONDisplay";
import ImageDisplay from "./ImageDisplay";
import { getPageDataWithBbox, getPageImage } from "../../../utils/Api";
import { transformToLegacyFormat } from "../../../utils/transformData";

type DisplayPredictionsProps = {
  documentID: string;
  pageID: string | null;
  mode?: "image-only" | "json-only" | "both";
  searchQuery?: string;
  highlightDuration?: number;
  isCurrentSearchResult?: boolean;
  currentOccurrenceIndex?: number | undefined;
  selectedTerm?: string;
  onTermSelect?: (term: string) => void;
  onHoverObject?: (uuid: string | null) => void;
  highlightedObjectUuid?: string | null | undefined;
  bboxVisibility?: { text: boolean; molecules: boolean; tables: boolean } | undefined;
};

type PageData = {
  raw_data: any;
  readable_data: any;
};

const DisplayPredictions: React.FC<DisplayPredictionsProps> = ({
  documentID,
  pageID,
  mode = "both",
  searchQuery,
  highlightDuration,
  isCurrentSearchResult = false,
  currentOccurrenceIndex,
  selectedTerm,
  onTermSelect,
  onHoverObject,
  highlightedObjectUuid,
  bboxVisibility,
}) => {
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [imageSrc, setImageSrc] = useState<string>("");
  const [rawData, setRawData] = useState<any>({});
  const [readableData, setReadableData] = useState<any>({});

  useEffect(() => {
    if (!imageSrc) {
      setLoadedImage(null);
      return;
    }

    const img = new Image();
    img.onload = () => setLoadedImage(img);
    img.src = imageSrc;

    return () => {
      img.onload = null;
    };
  }, [imageSrc]);

  useEffect(() => {
    if (!pageID) return;

    let cancelled = false;

    const fetchImage = async () => {
      try {
        const { data } = await getPageImage(documentID, pageID);
        if (!cancelled) {
          setImageSrc(data.image);
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Error fetching image:", error);
        }
      }
    };

    fetchImage();

    return () => {
      cancelled = true;
    };
  }, [documentID, pageID]);

  useEffect(() => {
    if (!pageID) return;

    let cancelled = false;

    const fetchJson = async () => {
      try {
        const { status, data }: { status: number; data: PageData } =
          await getPageDataWithBbox(documentID, pageID);
        if (!cancelled) {
          if (status === 206) {
            console.info("Results not ready...");
            setRawData({});
            setReadableData({});
          } else {
            setRawData(transformToLegacyFormat(data.raw_data));
            setReadableData(data.readable_data);
          }
        }
      } catch (error: any) {
        if (!cancelled) {
          console.error(`Error: ${error.message}`);
        }
      }
    };

    fetchJson();

    return () => {
      cancelled = true;
    };
  }, [documentID, pageID]);

  if (mode === "image-only") {
    return (
      <div className="h-full w-full">
        <ImageDisplay
          data={rawData}
          image={loadedImage}
          {...(selectedTerm && { selectedTerm })}
          {...(onHoverObject && { onHoverObject })}
          highlightedObjectUuid={highlightedObjectUuid}
          bboxVisibility={bboxVisibility}
        />
      </div>
    );
  }

  if (mode === "json-only") {
    return (
      <div className="h-full w-full p-4">
        <JSONDisplay
          data={readableData}
          {...(searchQuery !== undefined && { searchQuery })}
          {...(highlightDuration !== undefined && { highlightDuration })}
          isCurrentSearchResult={isCurrentSearchResult}
          currentOccurrenceIndex={currentOccurrenceIndex}
          {...(onTermSelect && { onTermSelect })}
        />
      </div>
    );
  }

  return (
    <div className="h-full w-full relative">
      <ImageDisplay
        data={rawData}
        image={loadedImage}
        {...(selectedTerm && { selectedTerm })}
        {...(onHoverObject && { onHoverObject })}
      />
      <div className="absolute bottom-0 left-0 right-0 h-2/5 bg-white/70 backdrop-blur-sm p-4 border-t border-slate-200 shadow-2xl">
        <JSONDisplay
          data={readableData}
          {...(searchQuery !== undefined && { searchQuery })}
          {...(highlightDuration !== undefined && { highlightDuration })}
          isCurrentSearchResult={isCurrentSearchResult}
          currentOccurrenceIndex={currentOccurrenceIndex}
          {...(onTermSelect && { onTermSelect })}
        />
      </div>
    </div>
  );
};

export default DisplayPredictions;
